import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Popover,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TuneIcon from '@mui/icons-material/Tune';
import type { LadderBand } from '../../types/sniper.types';
import {
  LADDER_BAND_TEMPLATES,
  MAX_LADDER_BANDS,
  defaultLadderBands,
  gatePct,
  ladderInvalidReason,
  ladderSummary,
  normalizeDecimalInput,
} from '../../utils/sniperArm';

/** Everything the popover edits — the ladder plus the legacy VAR exit prices. */
export interface LadderDraft {
  ladderEnabled: boolean;
  ladderBands: LadderBand[];
  /** Dollars, or `null` while unset (the ladder cannot be saved without one). */
  floorPrice: number | null;
  /**
   * Dollars below our own buy vwap the Phase-1 "VAR under review" preventive
   * sell rests at. Only meaningful while `ladderEnabled`.
   */
  varPreventiveExitOffset: number;
  /** Legacy one-shot VAR exit price — used when the ladder is OFF. */
  varExitPrice: number;
  /** Legacy one-shot confirmed-cancellation floor — used when the ladder is OFF. */
  varConfirmedFloorPrice: number;
}

/**
 * The exit-config popover: the confidence ladder (a rung table plus the single
 * dump floor) and, while the ladder is OFF, the legacy one-shot VAR exit prices
 * the ladder replaces.
 *
 * It lives in a popover rather than inline because the sniper row is already at
 * its width budget, and because a ladder is a TABLE — the rungs only make sense
 * read together, with their ordering and anti-self-cross rules visible at once.
 *
 * Editing is staged: `value` seeds a local draft, Apply hands the whole draft
 * back, Cancel throws it away. Apply is disabled while the ladder is on and
 * invalid, so the operator sees the exact rule they broke instead of a 400.
 */
export function SniperLadderPopover({
  value,
  onApply,
  disabled,
  /** Rendered instead of the default chip (the bulk/import bars pass a button). */
  renderTrigger,
  /** Kalshi-only in V1 — a Polymarket market shows why the toggle is inert. */
  venueSupportsLadder = true,
  /**
   * Whether the ladder-OFF branch edits the legacy VAR prices. The bulk and
   * import bars already carry their own Exit $ / Floor $ fields, so they turn
   * this off rather than offer a second, competing copy of them.
   */
  showLegacyExit = true,
}: {
  value: LadderDraft;
  onApply: (next: LadderDraft) => void;
  disabled?: boolean;
  renderTrigger?: (open: (e: React.MouseEvent<HTMLElement>) => void) => React.ReactNode;
  venueSupportsLadder?: boolean;
  showLegacyExit?: boolean;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<LadderDraft>(value);
  const open = Boolean(anchorEl);

  // Re-seed on every OPEN, not on every `value` change: a server refetch while
  // the popover is open must not wipe what the operator is typing.
  const openPopover = (e: React.MouseEvent<HTMLElement>) => {
    setDraft(value);
    setAnchorEl(e.currentTarget);
  };
  useEffect(() => {
    if (!open) setDraft(value);
  }, [open, value]);

  const invalid = useMemo(
    () =>
      draft.ladderEnabled
        ? ladderInvalidReason(
            draft.ladderBands,
            draft.floorPrice,
            draft.varPreventiveExitOffset,
          )
        : null,
    [
      draft.ladderEnabled,
      draft.ladderBands,
      draft.floorPrice,
      draft.varPreventiveExitOffset,
    ],
  );

  const setBand = (i: number, patch: Partial<LadderBand>) =>
    setDraft((d) => ({
      ...d,
      ladderBands: d.ladderBands.map((b, j) => (j === i ? { ...b, ...patch } : b)),
    }));

  const addBand = () =>
    setDraft((d) => {
      const last = d.ladderBands[d.ladderBands.length - 1];
      // Seed the new rung one cent above the last one so it starts VALID —
      // the operator adjusts from a working ladder instead of a broken one.
      // Capped at 1 (the validator's own (0,1] bound), NOT 0.99 (Kalshi's
      // tradeable ceiling, which the engine clamps separately at execution
      // time) — a last rung already at 0.99 would otherwise seed an
      // IDENTICAL price, which the "must increase" rule then rejects on
      // arrival.
      const next: LadderBand = last
        ? {
            maxConfidence: Math.min(1, Number((last.maxConfidence + 0.2).toFixed(2))),
            buyPrice: Math.min(1, Number((last.buyPrice + 0.01).toFixed(2))),
            sellPrice: Math.min(1, Number((last.sellPrice + 0.01).toFixed(2))),
          }
        : defaultLadderBands()[0];
      return { ...d, ladderBands: [...d.ladderBands, next] };
    });

  const removeBand = (i: number) =>
    setDraft((d) => ({
      ...d,
      ladderBands: d.ladderBands.filter((_, j) => j !== i),
    }));

  // ── V1 rollout: rungs 2 and 3 are individually enable/disable-able ──
  //
  // The wire has no per-rung "enabled" flag — a rung is enabled iff the array
  // reaches its index, and the array is always contiguous (rung 3 cannot
  // exist without rung 2). So "enable rung i+1" appends its TEMPLATE, and
  // "disable rung i+1" truncates the array back to i — dropping it and any
  // rung after it, since a later rung can never outlive the one that gates it.
  const enableNextTemplatedRung = () =>
    setDraft((d) => {
      const i = d.ladderBands.length;
      const template = LADDER_BAND_TEMPLATES[i];
      if (!template) return d; // already past the 3 templated rungs
      return { ...d, ladderBands: [...d.ladderBands, { ...template }] };
    });
  const disableRungsFrom = (i: number) =>
    setDraft((d) => ({ ...d, ladderBands: d.ladderBands.slice(0, i) }));

  const apply = () => {
    if (invalid) return;
    onApply(draft);
    setAnchorEl(null);
  };

  return (
    <>
      {renderTrigger ? (
        renderTrigger(openPopover)
      ) : (
        <Tooltip
          title={
            value.ladderEnabled
              ? 'Confidence ladder — click to edit the bands and the floor'
              : 'Exit config — one-shot VAR prices, or turn the confidence ladder on'
          }
        >
          <Chip
            size="small"
            icon={<TuneIcon sx={{ fontSize: 12 }} />}
            label={
              value.ladderEnabled
                ? ladderSummary(value.ladderBands, value.floorPrice)
                : 'one-shot'
            }
            onClick={openPopover}
            disabled={disabled}
            aria-label="exit config"
            sx={{
              bgcolor: value.ladderEnabled ? '#8e5bf5' : 'transparent',
              border: value.ladderEnabled ? 'none' : '1px solid',
              borderColor: 'divider',
              color: value.ladderEnabled ? '#fff' : 'text.secondary',
              fontWeight: 700,
              height: 20,
              fontSize: 10,
              cursor: 'pointer',
              '& .MuiChip-label': { px: 0.75 },
              '& .MuiChip-icon': { ml: 0.5, mr: -0.25 },
            }}
          />
        </Tooltip>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, minWidth: 380 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 0.5 }}
          >
            <Typography variant="subtitle2" sx={{ fontSize: 12.5, fontWeight: 700 }}>
              Confidence ladder
            </Typography>
            <Tooltip
              title={
                venueSupportsLadder
                  ? 'Chase the band price as confidence rises, resting a sell above it'
                  : 'Kalshi only in V1 — Polymarket has no amend, so it keeps the one-shot path'
              }
            >
              <span>
                <Switch
                  size="small"
                  checked={draft.ladderEnabled}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      ladderEnabled: e.target.checked,
                      // Turning it on with nothing configured starts from the
                      // defaults rather than an empty (unsaveable) table.
                      ladderBands:
                        e.target.checked && d.ladderBands.length === 0
                          ? defaultLadderBands()
                          : d.ladderBands,
                    }))
                  }
                  disabled={!venueSupportsLadder}
                  inputProps={{ 'aria-label': 'ladder enabled' }}
                />
              </span>
            </Tooltip>
          </Stack>

          {!venueSupportsLadder && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: 10.5, display: 'block', mb: 1 }}
            >
              Kalshi only in V1 — this market keeps the one-shot sweep.
            </Typography>
          )}

          {draft.ladderEnabled ? (
            <>
              <Stack direction="row" spacing={0.75} sx={{ mb: 0.5, px: 0.25 }}>
                <HeaderCell width={72} label="Up to %" />
                <HeaderCell width={72} label="Buy $" />
                <HeaderCell width={72} label="Sell $" />
              </Stack>
              <Stack spacing={0.75}>
                {/* Rungs 1–3: V1 rollout ships rung 1 only, with 2 and 3
                    individually enabled later. Rung 1 has no toggle — the
                    ladder can never run with zero rungs. */}
                {[0, 1, 2].map((i) => {
                  const band = draft.ladderBands[i];
                  if (band) {
                    return (
                      <Stack
                        // Rungs have no identity of their own — the index IS
                        // the rung, and the array is rewritten whole on every
                        // edit.
                        key={i}
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                      >
                        <PctCell
                          value={band.maxConfidence}
                          onChange={(maxConfidence) => setBand(i, { maxConfidence })}
                          ariaLabel={`band ${i + 1} max confidence`}
                          // The last rung is open-ended: anything at or above
                          // its threshold stays on it, so the number is only a
                          // boundary.
                          hint={
                            i === draft.ladderBands.length - 1
                              ? 'open-ended above this'
                              : undefined
                          }
                        />
                        {/* A rung price is never absent, so a cleared field
                            reads as 0 — out of (0,1], which the validation
                            then names. */}
                        <PriceCell
                          value={band.buyPrice}
                          onChange={(v) => setBand(i, { buyPrice: v ?? 0 })}
                          ariaLabel={`band ${i + 1} buy price`}
                        />
                        <PriceCell
                          value={band.sellPrice}
                          onChange={(v) => setBand(i, { sellPrice: v ?? 0 })}
                          ariaLabel={`band ${i + 1} sell price`}
                        />
                        {i === 0 ? (
                          <Box sx={{ width: 34 }} />
                        ) : (
                          <Tooltip title={`Disable rung ${i + 1}`}>
                            <Switch
                              size="small"
                              checked
                              onChange={() => disableRungsFrom(i)}
                              inputProps={{ 'aria-label': `band ${i + 1} enabled` }}
                              sx={{ ml: -0.5 }}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    );
                  }
                  // No rung at this index yet — a collapsed row offering just
                  // the toggle. Only the IMMEDIATE next rung is enableable
                  // (rung 3 needs rung 2 first), so it's disabled otherwise.
                  const isNext = i === draft.ladderBands.length;
                  return (
                    <Stack key={i} direction="row" spacing={0.75} alignItems="center">
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ fontSize: 11, flex: 1 }}
                      >
                        Rung {i + 1} — off
                      </Typography>
                      <Tooltip
                        title={
                          isNext
                            ? `Enable rung ${i + 1}`
                            : `Enable rung ${i} first`
                        }
                      >
                        <span>
                          <Switch
                            size="small"
                            checked={false}
                            disabled={!isNext}
                            onChange={() => isNext && enableNextTemplatedRung()}
                            inputProps={{ 'aria-label': `band ${i + 1} enabled` }}
                            sx={{ ml: -0.5 }}
                          />
                        </span>
                      </Tooltip>
                    </Stack>
                  );
                })}

                {/* Beyond the 3 templated rungs — the old free-form add/remove,
                    for an operator who wants more than the V1 default 3. */}
                {draft.ladderBands.slice(3).map((band, j) => {
                  const i = j + 3;
                  return (
                    <Stack key={i} direction="row" spacing={0.75} alignItems="center">
                      <PctCell
                        value={band.maxConfidence}
                        onChange={(maxConfidence) => setBand(i, { maxConfidence })}
                        ariaLabel={`band ${i + 1} max confidence`}
                        hint={
                          i === draft.ladderBands.length - 1
                            ? 'open-ended above this'
                            : undefined
                        }
                      />
                      <PriceCell
                        value={band.buyPrice}
                        onChange={(v) => setBand(i, { buyPrice: v ?? 0 })}
                        ariaLabel={`band ${i + 1} buy price`}
                      />
                      <PriceCell
                        value={band.sellPrice}
                        onChange={(v) => setBand(i, { sellPrice: v ?? 0 })}
                        ariaLabel={`band ${i + 1} sell price`}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeBand(i)}
                        aria-label={`remove band ${i + 1}`}
                        sx={{ p: 0.25 }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Stack>
                  );
                })}
              </Stack>

              {draft.ladderBands.length >= 3 && (
                <Button
                  size="small"
                  startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                  onClick={addBand}
                  disabled={draft.ladderBands.length >= MAX_LADDER_BANDS}
                  aria-label="add band"
                  sx={{ mt: 0.5, fontSize: 11 }}
                >
                  Add band
                </Button>
              )}

              <Divider sx={{ my: 1 }} />

              <Stack direction="row" spacing={0.75}>
                <Tooltip title="How far down the position is dumped when the goal falls over — it has to sit BELOW the market's pre-event price">
                  <span>
                    <PriceCell
                      label="Floor $"
                      width={90}
                      value={draft.floorPrice}
                      onChange={(floorPrice) => setDraft((d) => ({ ...d, floorPrice }))}
                      ariaLabel="ladder floor price"
                    />
                  </span>
                </Tooltip>
                <Tooltip title="Dollars below our own buy vwap the VAR-under-review preventive sell rests at — a small controlled loss aggressive enough to actually fill while the market reprices under review">
                  <span>
                    <PriceCell
                      label="VAR offset $"
                      width={90}
                      value={draft.varPreventiveExitOffset}
                      onChange={(v) =>
                        setDraft((d) => ({
                          ...d,
                          varPreventiveExitOffset: v ?? d.varPreventiveExitOffset,
                        }))
                      }
                      ariaLabel="ladder var preventive exit offset"
                    />
                  </span>
                </Tooltip>
              </Stack>
            </>
          ) : !showLegacyExit ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: 10.5, display: 'block' }}
            >
              Ladder off — the one-shot Exit $ / Floor $ fields apply.
            </Typography>
          ) : (
            <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }}>
              <Tooltip title="VAR exit — limit of the GTC sell posted if the goal is disallowed">
                <span>
                  <PriceCell
                    label="Exit $"
                    width={90}
                    value={draft.varExitPrice}
                    onChange={(v) =>
                      setDraft((d) => ({ ...d, varExitPrice: v ?? d.varExitPrice }))
                    }
                    ariaLabel="var exit price"
                  />
                </span>
              </Tooltip>
              <Tooltip title="VAR confirmed floor — Phase 2 aggressive-sell floor once the annulment is confirmed">
                <span>
                  <PriceCell
                    label="Floor $"
                    width={90}
                    value={draft.varConfirmedFloorPrice}
                    onChange={(v) =>
                      setDraft((d) => ({
                        ...d,
                        varConfirmedFloorPrice: v ?? d.varConfirmedFloorPrice,
                      }))
                    }
                    ariaLabel="var confirmed floor price"
                  />
                </span>
              </Tooltip>
            </Stack>
          )}

          {invalid && (
            <Typography
              variant="caption"
              sx={{ color: 'error.main', fontSize: 10.5, display: 'block', mt: 1 }}
            >
              {invalid}
            </Typography>
          )}

          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
            <Button
              size="small"
              onClick={() => setAnchorEl(null)}
              aria-label="cancel exit config"
              sx={{ fontSize: 11 }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={apply}
              disabled={!!invalid}
              aria-label="apply exit config"
              sx={{ fontSize: 11 }}
            >
              Apply
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
}

function HeaderCell({ width, label }: { width: number; label: string }) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ width, fontSize: 9.5, lineHeight: 1 }}
    >
      {label}
    </Typography>
  );
}

const cellSx = {
  '& .MuiInputBase-input': { py: 0.35, px: 0.75, fontSize: 12 },
  '& .MuiInputLabel-root': { fontSize: 10.5 },
  '& .MuiInputLabel-shrink': { fontSize: 11 },
};

/**
 * A dollar price cell. Kept as free TEXT while editing (so `0.` and a cleared
 * field survive a keystroke) and reported as a number — `null` when the field is
 * empty or not a number, which the ladder validation then names.
 */
function PriceCell({
  value,
  onChange,
  ariaLabel,
  label,
  width = 72,
}: {
  value: number | null;
  onChange: (value: number | null) => void;
  ariaLabel: string;
  label?: string;
  width?: number;
}) {
  const [text, setText] = useState(value == null ? '' : String(value));
  useEffect(() => {
    setText((prev) => (Number(prev) === value ? prev : value == null ? '' : String(value)));
  }, [value]);
  return (
    <TextField
      size="small"
      label={label}
      value={text}
      onChange={(e) => {
        const next = normalizeDecimalInput(e.target.value);
        setText(next);
        onChange(next.trim() === '' || !Number.isFinite(Number(next)) ? null : Number(next));
      }}
      inputProps={{ inputMode: 'decimal', 'aria-label': ariaLabel }}
      sx={{ width, ...cellSx }}
    />
  );
}

/** A confidence cell, edited as an INTEGER PERCENT over a 0..1 stored value. */
function PctCell({
  value,
  onChange,
  ariaLabel,
  hint,
}: {
  value: number;
  onChange: (value: number) => void;
  ariaLabel: string;
  hint?: string;
}) {
  const [text, setText] = useState(String(gatePct(value)));
  useEffect(() => {
    setText((prev) => (Number(prev) === gatePct(value) ? prev : String(gatePct(value))));
  }, [value]);
  return (
    <Tooltip title={hint ?? ''} disableHoverListener={!hint}>
      <TextField
        size="small"
        value={text}
        onChange={(e) => {
          const next = e.target.value.replace(/[^0-9]/g, '');
          setText(next);
          // Empty reads as 0, which the validation rejects as out of (0,1] —
          // the operator sees "confidence 0–100%" rather than a silent default.
          onChange(next === '' ? 0 : Number(next) / 100);
        }}
        inputProps={{ inputMode: 'numeric', maxLength: 3, 'aria-label': ariaLabel }}
        sx={{ width: 72, ...cellSx }}
      />
    </Tooltip>
  );
}
